import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	Role,
	SetupWizardUserFormPayload,
	USER_CSV_LABELS,
	USER_CSV_SPEC,
	UserCsvKeys,
	UserCsvTemplate,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { RoleDropdownField } from '../../../fields/setup-wizard/role-dropdown-field/role-dropdown-field';
import { FormUtil } from '../../../../utils/form.util';

type UserForm = {
	[UserCsvKeys.schoolIdOrEmail]: FormControl<string | null>;
	[UserCsvKeys.group]: FormControl<Role | null>;
	[UserCsvKeys.firstName]: FormControl<string | null>;
	[UserCsvKeys.lastName]: FormControl<string | null>;
	[UserCsvKeys.userUuid]: FormControl<string | null>;
	[UserCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-user-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		RoleDropdownField,
	],
	standalone: true,
	templateUrl: './setup-wizard-user-form.html',
	styleUrl: './setup-wizard-user-form.scss',
})
export class SetupWizardUserForm {
	public edit = input<UserCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardUserFormPayload>();
	public UserCsvKeys = UserCsvKeys;
	public USER_CSV_LABELS = USER_CSV_LABELS;
	public Validators = Validators;

	public form = new FormGroup<UserForm>({
		[UserCsvKeys.schoolIdOrEmail]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UserCsvKeys.group]: new FormControl<Role | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UserCsvKeys.firstName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UserCsvKeys.lastName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UserCsvKeys.userUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UserCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor() {
		FormUtil.applyCsvSpecValidators(this.form, USER_CSV_SPEC);
	}

	ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			// If edit contains a userUuid we can remove all validators for schoolIdOrEmail
			if (edit[UserCsvKeys.userUuid]) {
				this.form.get(UserCsvKeys.schoolIdOrEmail)?.clearValidators();
				this.form.get(UserCsvKeys.schoolIdOrEmail)?.updateValueAndValidity();
			}

			this.form.patchValue({
				[UserCsvKeys.schoolIdOrEmail]: edit[UserCsvKeys.schoolIdOrEmail],
				[UserCsvKeys.group]: edit[UserCsvKeys.group],
				[UserCsvKeys.firstName]: edit[UserCsvKeys.firstName],
				[UserCsvKeys.lastName]: edit[UserCsvKeys.lastName],
				[UserCsvKeys.userUuid]: edit[UserCsvKeys.userUuid],
				[UserCsvKeys.active]: edit[UserCsvKeys.active],
			});
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[UserCsvKeys.schoolIdOrEmail]: value[UserCsvKeys.schoolIdOrEmail] ?? undefined,
				[UserCsvKeys.group]: value[UserCsvKeys.group] ?? undefined,
				[UserCsvKeys.firstName]: value[UserCsvKeys.firstName] ?? undefined,
				[UserCsvKeys.lastName]: value[UserCsvKeys.lastName] ?? undefined,
				[UserCsvKeys.userUuid]: value[UserCsvKeys.userUuid] ?? undefined,
				[UserCsvKeys.active]: value[UserCsvKeys.active] ?? undefined,
			});

			this.form.reset();
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}
}
